import { defineComponent } from "vue";
import { Modal } from "bootstrap";
import Debug from "@/shared/support/Debug";
import OverlaySuppression from "@/shared/support/OverlaySuppression";
import TooltipSupport from "@/shared/support/Tooltip";

// Static activeModal stored outside the component
let activeModal: any = null;

export default defineComponent({
    name: "ModalBase",
    emits: ["modal-update"],
    data() {
        return {
            modal: null as Modal | null,
            loading: false,
            inited: false
        };
    },
    methods: {
        /**
         * Shows a modal.
         */
        showModal(): void {
            TooltipSupport.clearTooltips(); // forces any lingering tooltips to be removed
            // Acquired before the outgoing modal is killed, so a replacement never lets the screen go unclaimed.
            OverlaySuppression.acquire(this);
            killModals(); // bootstrap modals only support 1 modal at a time
            this.modal = new Modal(this.$el as Element, {
                backdrop: "static", // A backdrop which doesn't close the modal on click.
                keyboard: false, // Doesn't close the modal when escape key is pressed
                focus: true, // Puts the focus on the modal when initialized.
            });
            this.modal.show();
            Debug.verifyIds();
            activeModal = this;
        },
        /**
         * Hides the modal. Optionally sends a modal-update event to the owner of the modal. This could be used to reload a grid.
         * @param forceReload Sends an unload event to the owner of the modal. Default is false.
         */
        hideModal(forceReload?: boolean): boolean {
            activeModal = null;
            if (this.modal) {
                this.modal.hide();
                this.modal = null;
                if (forceReload === true)
                    this.$emit("modal-update");
                return true;
            }
            return false;
        },
        initializing(): boolean {
            return !this.inited;
        },
        initialized(): void {
            this.inited = true;
        },
        /**
         * Default Cancel button handler.
         * Doesn't send a modal-update event.
         */
        cancelClicked(): void {
            this.hideModal();
        }
    },
    mounted(): void {
        const element = this.$el as Element;
        // Hidden while still fading in, which Bootstrap ignores, so the hide is taken again once it has finished.
        element.addEventListener("shown.bs.modal", (): void => {
            if (!this.modal) Modal.getInstance(element)?.hide();
        });
        // Released once the modal has left the screen, unless it was shown again while fading out.
        element.addEventListener("hidden.bs.modal", (): void => {
            if (!this.modal) OverlaySuppression.release(this);
        });
    },
    unmounted(): void {
        OverlaySuppression.release(this);
    }
});

/**
 * Kills any active modal
 */
export function killModals(): void {
    if (activeModal)
        activeModal.hideModal();
}

/**
 * Gets the currently active modal
 */
export function getActiveModal(): any {
    return activeModal;
}
